import { z } from 'zod';

function validateLuhnChecksum(cardNumber: string): boolean {
  let sum = 0;
  let isEven = false;

  for (let i = cardNumber.length - 1; i >= 0; i -= 1) {
    let digit = parseInt(cardNumber[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
}

export const zodValidations = {
  email: z
    .string()
    .trim()
    .min(1, '入力必須です')
    .pipe(
      z.email({
        message: '正しいメールアドレスを入力してください',
      })
    ),

  loginId: z
    .string()
    .trim()
    .min(1, '入力必須です')
    .regex(/^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/, '半角英数字記号で入力してください')
    .min(6, '6文字以上で入力してください')
    .max(50, '50文字以下で入力してください'),

  // ログインIDは必須ではないが、入力された場合はバリデーションを行う
  loginIdOptional: z
    .string()
    .regex(/^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/, '半角英数字記号で入力してください')
    .optional()
    .refine(
      (value) => {
        if (value === undefined || value === null || value === '') {
          return true;
        }

        return value.length >= 6 && value.length <= 50;
      },
      {
        message: '6文字以上、50文字以下で入力してください',
      }
    ),

  password: z
    .string()
    .trim()
    .min(1, '入力必須です。')
    .regex(
      /^[0-9a-zA-Z-]*$/,
      '半角英数字で入力してください（半角ハイフンも利用可）'
    )
    .min(6, '6文字以上で入力してください')
    .max(20, '20文字以下で入力してください')
    .refine((val) => !/^[a-zA-Z-]+$/.test(val), {
      message: '半角英字・半角数字を含めて入力してください',
    })
    .refine((val) => !/^[0-9-]+$/.test(val), {
      message: '半角英字・半角数字を含めて入力してください',
    }),

  cardNumber: z
    .string()
    .min(1, '入力必須です。')
    .min(14, '14〜16文字で入力してください')
    .max(16, '14〜16文字で入力してください')
    .refine((val) => /^\d+$/.test(val), {
      message: '無効な文字が含まれています。',
    })
    .refine(
      (val) => {
        // 本番環境のみLuhnチェック実施（開発環境ではスキップ）
        const isDevEnvironment =
          typeof window !== 'undefined' &&
          window.location.hostname.endsWith('.unext.dev');
        return isDevEnvironment ? true : validateLuhnChecksum(val);
      },
      {
        message: 'クレジットカード番号が正しくありません。',
      }
    ),

  cardholderFullName: z
    .string()
    .min(1, '入力必須です')
    .transform((val) => val.trim())
    .refine((val) => !/\u3000/.test(val), {
      message: '全角スペースが含まれています',
    })
    .refine((val) => !/[a-z]/.test(val), {
      message: '大文字で入力してください',
    })
    .refine((val) => !/[0-9]/.test(val), {
      message: '英字で入力してください',
    })
    .refine((val) => /^[A-Z ]+$/.test(val), {
      message: '半角英字と半角スペースのみ入力可能です',
    })
    .refine((val) => /^[A-Z]+( [A-Z]+)+$/.test(val), {
      message: 'フォーマットが正しくありません。例: TARO YAMADA',
    }),
  cardExpireMonth: z.string().min(1, '入力必須です'),
  cardExpireYear: z.string().min(1, '入力必須です'),
  cardSecurityCode: z
    .string()
    .min(1, '入力必須です')
    .regex(/^[0-9]+$/, '3～4桁の半角数字で入力してください')
    .min(3, '3～4桁の半角数字で入力してください')
    .max(4, '3～4桁の半角数字で入力してください'),

  verificationCode: z
    .string()
    .min(1, '入力必須です')
    .regex(/^[0-9a-zA-Z]+$/, '半角英数字で入力してください'),

  securityCodeAndPinCode: z
    .string()
    .min(1, '入力必須です')
    .regex(/^[0-9]+$/, '半角数字で入力してください')
    .length(4, '4文字で入力してください'),
} as const;
